import React, { useEffect, useState } from "react";

import { Interweave } from "interweave";
import { Emoji } from "emoji-mart";
import {
  ColorWrapper,
  Container,
  IconPreview,
  Line,
  OverlinePreview,
  TextPreview,
  TitlePreview,
} from "./styled";
import { Alignment } from "@Shared/Alignment-Size";
import ViewMode from "@Shared/ViewMode";
import { setCaretOnContentEditable } from "@Shared/Functions/set-caret-contenteditable";
import QuillEditor from "@Shared/Components/MacroSidebar/QuillEditor";
import Icon, { getIconType } from "../../../../components/ui/icon/Icon";
import { useTitleTextStore } from "../../../../stores/use-titletext-store";
import { TITLETEXT_PROPS } from "../../../../optics/components/drawers/settings/features/text-and-title/defaults";
import { useTheme } from "styled-components";

function TitleAndText({ data, updateContent = () => {}, viewMode }) {
  const { setData } = useTitleTextStore((state) => ({
    setData: state.setData,
  }));

  const [oldOverlineColor, setOldOverlineColor] = useState("");
  const [oldTitleColor, setOldTitleColor] = useState("");
  const [oldTextColor, setOldTextColor] = useState("");

  const {
    colorsByColorScheme,
    alignment,
    overlineSize,
    titleSize,
    textSize,
    iconSize,
    layout,
    emoji,
    emojiEnabled,
    titleIcon,
    lineWidth,
    lineWeight,
  } = data;

  const theme = useTheme();

  const iconType = getIconType(titleIcon);

  const { overlineColor, titleColor, textColor, lineColor, iconColor } =
    colorsByColorScheme[theme.name];

  const getCorrectAlignment = (alignmentForText = false) => {
    if (alignment === Alignment.LEFT) {
      return alignmentForText ? "left" : "flex-start";
    }
    if (alignment === Alignment.CENTER) {
      return "center";
    }
    return alignmentForText ? "right" : "flex-end";
  };

  const extractStyleFromHTMLString = (string, setter) => {
    const dom = new DOMParser().parseFromString(string, "text/html");
    const styles = dom.querySelectorAll("[style]");

    if (styles[0]?.style?.color) {
      setter(styles[0].style.color);
    }
  };

  useEffect(() => {
    if (viewMode === ViewMode.EDIT) {
      const titleWrapperElement = document.querySelector(
        "#title-editor .ql-editor"
      );

      if (oldOverlineColor) {
        setData({ [TITLETEXT_PROPS.OVERLINE_COLOR]: oldOverlineColor });
      }

      if (oldTitleColor) {
        setData({ [TITLETEXT_PROPS.TITLE_COLOR]: oldTitleColor });
      }

      if (oldTextColor) {
        setData({ [TITLETEXT_PROPS.TEXT_COLOR]: oldTextColor });
      }

      setTimeout(() => {
        setCaretOnContentEditable(titleWrapperElement, false);
      }, 250);
    }
  }, [viewMode]);

  useEffect(() => {
    const DEFAULT_COLOR = "#0052CC";
    const DEFAULT_FONT_COLOR = "#172b4d";

    if (overlineColor === DEFAULT_COLOR || !overlineColor) {
      extractStyleFromHTMLString(
        data[TITLETEXT_PROPS.OVERLINE],
        setOldOverlineColor
      );
    }
    if (titleColor === DEFAULT_FONT_COLOR || !titleColor) {
      extractStyleFromHTMLString(data[TITLETEXT_PROPS.TITLE], setOldTitleColor);
    }
    if (textColor === DEFAULT_FONT_COLOR || !textColor) {
      extractStyleFromHTMLString(data[TITLETEXT_PROPS.TEXT], setOldTextColor);
    }
  }, [data]);

  return (
    <Container
      alignment={getCorrectAlignment()}
      textAlign={getCorrectAlignment(true)}
      overlineSize={overlineSize}
      titleSize={titleSize}
      textSize={textSize}
    >
      {layout.overline && (
        <ColorWrapper color={iconColor}>
          {viewMode === ViewMode.EDIT ? (
            <QuillEditor
              id="overline-editor"
              text={data[TITLETEXT_PROPS.OVERLINE]}
              setText={(value) => {
                updateContent({ value, prop: TITLETEXT_PROPS.OVERLINE });
              }}
              enableColors={false}
            />
          ) : (
            <OverlinePreview
              overlineSize={overlineSize}
              alignment={getCorrectAlignment(true)}
            >
              <Interweave content={data[TITLETEXT_PROPS.OVERLINE]} />
            </OverlinePreview>
          )}
        </ColorWrapper>
      )}

      {(layout.title || layout.icon) && (
        <ColorWrapper>
          {viewMode === ViewMode.EDIT ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                columnGap: "0.5rem",
              }}
            >
              <>
                {layout.icon && (
                  <IconPreview color={iconColor} iconSize={iconSize}>
                    {Object.keys(emoji).length && emojiEnabled ? (
                      <Emoji emoji={emoji} set="twitter" size={iconSize} />
                    ) : (
                      <>
                        {iconType === "app" ? (
                          <img src={titleIcon} alt="img" height={iconSize} />
                        ) : (
                          <Icon icon={titleIcon} size={iconSize} />
                        )}
                      </>
                    )}
                  </IconPreview>
                )}
                {layout.title && (
                  <QuillEditor
                    id="title-editor"
                    text={data[TITLETEXT_PROPS.TITLE]}
                    setText={(value) => {
                      updateContent({ value, prop: TITLETEXT_PROPS.TITLE });
                    }}
                    enableColors={false}
                  />
                )}
              </>
            </div>
          ) : (
            <TitlePreview
              titleSize={titleSize}
              alignment={getCorrectAlignment(true)}
            >
              {layout.icon && (
                <IconPreview color={iconColor} iconSize={iconSize}>
                  {Object.keys(emoji).length && emojiEnabled ? (
                    <Emoji emoji={emoji} set="twitter" size={iconSize} />
                  ) : (
                    <>
                      {iconType === "app" ? (
                        <img src={titleIcon} alt="img" height={iconSize} />
                      ) : (
                        <Icon icon={titleIcon} size={iconSize} />
                      )}
                    </>
                  )}
                </IconPreview>
              )}
              {layout.title && (
                <Interweave content={data[TITLETEXT_PROPS.TITLE]} />
              )}
            </TitlePreview>
          )}
        </ColorWrapper>
      )}

      {layout.line && lineWidth > 0 && lineWeight > 0 && (
        <Line
          lineColor={iconColor}
          lineWidth={lineWidth}
          lineWeight={lineWeight}
        />
      )}

      {layout.text && (
        <ColorWrapper>
          {viewMode === ViewMode.EDIT ? (
            <QuillEditor
              id="text-editor"
              text={data[TITLETEXT_PROPS.TEXT]}
              setText={(value) => {
                updateContent({ prop: TITLETEXT_PROPS.TEXT, value });
              }}
              enableColors={false}
            />
          ) : (
            <TextPreview
              textSize={textSize}
              alignment={getCorrectAlignment(true)}
            >
              <Interweave content={data[TITLETEXT_PROPS.TEXT]} />
            </TextPreview>
          )}
        </ColorWrapper>
      )}
    </Container>
  );
}

export default TitleAndText;
